import { formatCurrency } from "./currencyUtils";

export interface MedicalProvider {
    company: string;
    bill: number;
    balanceDescription: string;
    reduction: number;
    accountNumber?: string | null;
}

export interface InsuranceCompany {
    company: string;
    bill: number;
    statusDescription: string;
    reduction: number;
    accountNumber?: string | null;
}

export interface DisbursementRow {
    idUUID: string;
    company: string;
    accountNumber?: string | null;
    type: string;
    bill: string | number;
    insurance: string | number;
    adj: string | number;
    self: string | number;
    medPay: string | number;
    currentBalance: string | number;
    reduction: string | number;
    source: "provider" | "loan" | "insurance";
}

export interface CostData {
    bill: string;
    insurance: string;
    adj: string;
    self: string;
    medPay: string;
    currentBalance: string;
    reduction: string;
    sent: string;
    approved: string;
}

/**
 * Parse value to number for calculations
 */
const parseValue = (value: string | number | null | undefined): number => {
    if (value == null) {
        return 0;
    }
    if (typeof value === "number") {
        return value;
    }
    const cleaned = value.replace(/[$,]/g, "");
    const num = Number.parseFloat(cleaned);
    return Number.isNaN(num) ? 0 : num;
};

/**
 * Generate balance description for medical providers
 */
const generateBalanceDescription = (medPay: number): string => {
    if (medPay > 0) {
        return `Balance after MedPay of ${formatCurrency(medPay)}, Reduced to`;
    }
    return "Reduced to";
};

/**
 * Generate status description for insurance companies
 */
const generateStatusDescription = (type: string, insurance: number, medPay: number): string => {
    if (type === "Insurance" && insurance > 0) {
        return "Paid by Health Insurance";
    }
    if (medPay > 0) {
        return `Balance after MedPay of ${formatCurrency(medPay)}, Reduced to`;
    }
    return "Reduced to";
};

/**
 * Transform disbursement rows into medical providers list
 */
export const mapMedicalProviders = (rows: DisbursementRow[]): MedicalProvider[] => {
    const medicalProviderRows = rows.filter((row) => row.source === "provider");

    return medicalProviderRows.map((row) => {
        const medPay = parseValue(row.medPay);
        const bill = parseValue(row.bill);
        const reduction = parseValue(row.reduction);

        return {
            company: row.company,
            bill,
            balanceDescription: generateBalanceDescription(medPay),
            reduction,
            accountNumber: row.accountNumber,
        };
    });
};

/**
 * Transform insurance companies array
 */
export const mapInsuranceCompanies = (rows: DisbursementRow[]): InsuranceCompany[] => {
    const insuranceRows = rows.filter((row) => row.source === "insurance" || row.source === "loan");

    return insuranceRows.map((row) => {
        const insurance = parseValue(row.insurance);
        const medPay = parseValue(row.medPay);
        const bill = parseValue(row.bill);
        const reduction = parseValue(row.reduction);

        return {
            company: row.company,
            bill,
            statusDescription: generateStatusDescription(row.type, insurance, medPay),
            reduction,
            accountNumber: row.accountNumber,
        };
    });
};

/**
 * Calculate total specials (sum of all medical provider bills)
 */
export const calculateTotalSpecials = (rows: DisbursementRow[]): number => {
    const medicalProviderRows = rows.filter((row) => row.source === "provider");
    return medicalProviderRows.reduce((sum, row) => sum + parseValue(row.bill), 0);
};

/**
 * Get expense amount from COST data
 */
export const getExpenseAmount = (costData: CostData): number => {
    return parseValue(costData.reduction);
};
